import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import ms from 'ms';
import { type ComponentProps } from 'react';
import { startTransition, useDeferredValue, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { ReactSelectInput } from '@hivebrite/design-system/Inputs';

import { useCurrentNetwork } from 'hb-react/shared/contexts';
import { axiosWithSyntaxHandlers } from 'hb-react/shared/utils/axios';
import { captureException } from 'hb-react/shared/utils/errorReporting';
import { type Topic as BaseTopic } from 'hb-react/types';

type Topic = Pick<BaseTopic, 'id' | 'name'>;

interface TopicOption {
  label: Topic['name'];
  value: Topic['id'];
}

const addValidTopic = (
  topic: Topic | null | undefined,
  uniqueTopicsMap: Map<number, Topic>,
) => {
  if (topic?.id && !!topic?.name) {
    uniqueTopicsMap.set(topic.id, topic);
  }
};

const ReactSelectTopics = ({
  id,
  name,
  ...props
}: ComponentProps<typeof ReactSelectInput>) => {
  const {
    currentNetwork: { id: networkId },
  } = useCurrentNetwork();

  const [inputValue, setInputValue] = useState('');
  const defferedInputValue = useDeferredValue(inputValue);

  const queryClient = useQueryClient();

  const { data: topics = [], isLoading } = useQuery({
    queryKey: ['topics', networkId, defferedInputValue],
    queryFn: () =>
      axiosWithSyntaxHandlers
        .get(Routes.backoffice_network_topics_path(networkId), {
          params: {
            fulltext: defferedInputValue,
            format: 'json',
            per_page: 25,
          },
        })
        .then((res) => res.data),
    staleTime: ms('1h'),
    placeholderData: keepPreviousData,
  });

  const onInputChange = useDebouncedCallback((value, { action }) => {
    if (action === 'input-change') {
      startTransition(() => {
        setInputValue(value);
      });
    }
  }, 500);

  const uniqueTopicsMap = useMemo(() => {
    const map = new Map<number, Topic>();

    try {
      topics?.forEach((topic) => addValidTopic(topic, map));

      const cachedQueries = queryClient.getQueriesData({
        queryKey: ['topics', networkId],
      });

      cachedQueries.forEach(([_queryKey, cachedData]) => {
        if (Array.isArray(cachedData)) {
          cachedData.forEach((topic) => addValidTopic(topic, map));
        }
      });

      const selectedValues = Array.isArray(props.value)
        ? props.value
        : [props.value];

      selectedValues.forEach((value) => addValidTopic(value, map));

      return map;
    } catch (error) {
      captureException(error);

      return new Map();
    }
  }, [props.value, topics, queryClient, networkId]);

  const options = useMemo(
    (): Array<TopicOption> =>
      Array.from(uniqueTopicsMap.values()).map((topic) => ({
        value: topic.id,
        label: topic.name,
      })),
    [uniqueTopicsMap],
  );

  return (
    <ReactSelectInput
      placeholder={__('Search and select groups')}
      isMulti
      {...props}
      id={id}
      name={name}
      options={options}
      isLoading={isLoading}
      // @ts-expect-error react-select is not typed
      onInputChange={onInputChange}
    />
  );
};

export default ReactSelectTopics;
